import React from "react";
import { ScrollText, ShieldCheck, AlertTriangle } from "lucide-react";
import { PageHeader, SectionCard, RagBadge } from "@/components/Shared";
import { compliance, ownership } from "@/data/mineData";

const dot = {
  green: "bg-emerald-500",
  amber: "bg-amber-500",
  red: "bg-red-500",
};

export default function Legal() {
  return (
    <div data-testid="page-legal">
      <PageHeader eyebrow="Legal & Compliance" title="Title, Permits & Governance" subtitle="Mining rights, licenses, environmental authorisations and ownership structure." />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <div className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm">
          <ShieldCheck size={20} className="text-emerald-600 mb-3" />
          <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Mining right status</div>
          <div className="font-display text-2xl font-semibold text-slate-900 mt-1">Valid · 2042</div>
          <p className="text-xs text-slate-500 mt-2">Renewed by DMRE under MPRDA Section 23.</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm">
          <ScrollText size={20} className="text-slate-700 mb-3" />
          <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Active licenses</div>
          <div className="font-display text-2xl font-semibold text-slate-900 mt-1">8 / 9</div>
          <p className="text-xs text-slate-500 mt-2">1 environmental authorisation in renewal queue.</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm">
          <AlertTriangle size={20} className="text-amber-600 mb-3" />
          <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Open obligations</div>
          <div className="font-display text-2xl font-semibold text-slate-900 mt-1">2</div>
          <p className="text-xs text-slate-500 mt-2">Surface lease re-negotiation (2 plots).</p>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <SectionCard title="Compliance Register" description="Permits, authorisations and statutory items" className="xl:col-span-2">
          <div className="space-y-2" data-testid="compliance-list">
            {compliance.map((c) => (
              <div key={c.area} className="flex items-center justify-between py-3 px-3 rounded-md hover:bg-slate-50 border border-transparent hover:border-slate-100 transition-colors">
                <div className="flex items-center gap-3 min-w-0">
                  <span className={`h-2 w-2 rounded-full flex-shrink-0 ${dot[c.status]}`} />
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-slate-900 truncate">{c.area}</div>
                    <div className="text-xs text-slate-500 truncate">{c.detail}</div>
                  </div>
                </div>
                <RagBadge level={c.status} />
              </div>
            ))}
          </div>
        </SectionCard>

        <SectionCard title="Ownership / BBBEE" description="Shareholder structure">
          <div className="space-y-3">
            {ownership.map((o) => (
              <div key={o.holder}>
                <div className="flex items-center justify-between text-sm mb-1.5">
                  <span className="text-slate-800 font-medium truncate pr-2">{o.holder}</span>
                  <span className="text-slate-900 font-semibold">{o.pct}%</span>
                </div>
                <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                  <div className="h-full bg-slate-900" style={{ width: `${o.pct}%` }} />
                </div>
              </div>
            ))}
          </div>
          <div className="mt-5 pt-5 border-t border-slate-100">
            <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-2">BBBEE status</div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-50 border border-emerald-200">
              <ShieldCheck size={14} className="text-emerald-600" />
              <span className="text-xs font-semibold text-emerald-700">Level 3 Verified · 2024-11</span>
            </div>
          </div>
        </SectionCard>
      </div>

      <div className="mt-6">
        <SectionCard title="Legal Notes" description="Material disclosures">
          <ul className="text-sm text-slate-700 space-y-2 list-disc pl-5">
            <li>No active litigation against the operating entity as of January 2026.</li>
            <li>Section 11 transfer approvals already secured for two adjacent farm portions.</li>
            <li>Royalty obligations to Limpopo Community Trust capped at 1.5% of gross revenue.</li>
            <li>Closure liability provision: R263m held in escrow with Standard Bank.</li>
          </ul>
        </SectionCard>
      </div>
    </div>
  );
}
